/**
 * NoZoom — désactive le zoom (pinch + double-tap) DANS l'app mobile marque blanche.
 *
 * POURQUOI un module dédié (et pas juste le <meta viewport>) : iOS WKWebView IGNORE
 * `user-scalable=no` / `maximum-scale` (choix d'Apple pour l'accessibilité depuis
 * iOS 10). Le meta seul bloque Android mais PAS le pinch iOS. La seule défense web
 * fiable sur iOS est d'intercepter les évènements `gesturestart`/`gesturechange`
 * (propres à Safari/WKWebView) et le double-tap. On combine donc 3 couches :
 *   1. meta viewport durci (couvre Android + renforce iOS) ;
 *   2. CSS `touch-action: pan-x pan-y` + `text-size-adjust:100%` (empêche le
 *      navigateur de proposer le zoom / l'ajustement de texte) ;
 *   3. listeners JS `gesturestart/change/end` + double-tap (LE point qui dompte iOS).
 * La couche vraiment 100% fiable sur iOS reste NATIVE (scrollView.maximumZoomScale=1,
 * cf. unisoft_mobile/src/WebApp.js) — ce module est la moitié web, indispensable pour
 * Android et pour bloquer le double-tap.
 *
 * INVARIANT NAVIGATEUR : tout est gardé par `window.ISAPP` (posé par le shell natif
 * AVANT le 1er load). Hors app → no-op TOTAL : on ne touche NI au viewport, NI au CSS,
 * NI aux listeners → le site web classique garde le zoom (accessibilité intacte).
 * SSR-safe : aucun accès à window hors `typeof window`.
 *
 * Idempotent : un seul install effectif, ré-appels ignorés.
 */

const STYLE_ID = "kehila-no-zoom-style";
let _installed = false;

function isApp() {
    if (typeof window === "undefined") return false;
    return window.ISAPP === true || !!window.ReactNativeWebView;
}

// Force le <meta name="viewport"> anti-zoom. Une page sans <Meta> (ou rendue avant
// que mobileConfig soit chargé) garde sinon le viewport par défaut → on garantit ici
// qu'il est TOUJOURS posé en app, sur toutes les pages.
function enforceViewport() {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "viewport");
        document.head.appendChild(meta);
    }
    meta.setAttribute(
        "content",
        "width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no,viewport-fit=cover"
    );
}

// CSS : interdit le geste de zoom au moteur (touch-action) et l'ajustement auto de la
// taille du texte iOS. `pan-x pan-y` autorise le SCROLL mais pas le pinch-zoom.
function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
        html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
        html, body { touch-action: pan-x pan-y; }
    `;
    document.head.appendChild(style);
}

// Bloque le pinch iOS (gesture* = WKWebView/Safari) + le double-tap-to-zoom.
function installListeners() {
    // iOS pinch : preventDefault sur gesturestart suffit à tuer le zoom WKWebView.
    const blockGesture = (e) => { e.preventDefault(); };
    document.addEventListener("gesturestart", blockGesture, { passive: false });
    document.addEventListener("gesturechange", blockGesture, { passive: false });
    document.addEventListener("gestureend", blockGesture, { passive: false });

    // Double-tap-to-zoom : on annule un 2e tap rapproché (< 300ms). On ne bloque que
    // le ZOOM (preventDefault sur le 2e touchend), pas le tap simple → les clics
    // restent normaux.
    let lastTouchEnd = 0;
    document.addEventListener("touchend", (e) => {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
            e.preventDefault();
        }
        lastTouchEnd = now;
    }, { passive: false });

    // Filet supplémentaire : un touchmove à 2 doigts = pinch → on annule.
    document.addEventListener("touchmove", (e) => {
        if (e.touches && e.touches.length > 1) {
            e.preventDefault();
        }
    }, { passive: false });
}

/**
 * Installe le blocage du zoom. À appeler UNE fois au boot (depuis _app.js). No-op
 * hors app, idempotent. Renvoie une fonction de cleanup (rarement utile : l'app vit
 * tout le temps), no-op si rien n'a été installé.
 */
export function installNoZoom() {
    if (_installed || !isApp() || typeof document === "undefined") return () => {};
    _installed = true;

    enforceViewport();
    injectStyle();
    installListeners();

    // Re-pose le viewport après chaque navigation SPA : une page qui rend son propre
    // <Meta> écraserait le nôtre par le viewport conditionnel (identique en app, mais
    // on garantit l'invariant même si une page oublie le mode app).
    let unbind = () => {};
    try {
        const Router = require("next/router").default;
        const onRoute = () => enforceViewport();
        Router.events.on("routeChangeComplete", onRoute);
        unbind = () => Router.events.off("routeChangeComplete", onRoute);
    } catch (_) {}

    return unbind;
}

export default { installNoZoom };
